import { z } from 'zod';

/**
 * Customer Segment Enum
 * - NEW: First booking ever with this vendor
 * - REGULAR: 2+ bookings, active in last 60 days
 * - VIP: High value (top 10% by LTV or 5+ bookings)
 * - AT_RISK: No visit in 60+ days
 */
export const CustomerSegmentEnum = z.enum(['NEW', 'REGULAR', 'VIP', 'AT_RISK']);
export type CustomerSegment = z.infer<typeof CustomerSegmentEnum>;

/**
 * Booking Pattern Summary for a customer
 */
export const BookingPatternSchema = z.object({
  preferredTimeSlots: z.array(z.string()), // e.g., ["10:00-12:00", "14:00-16:00"]
  preferredDaysOfWeek: z.array(z.number()), // 0=Sunday, 6=Saturday
  averageGroupSize: z.number(),
  popularAddons: z.array(
    z.object({
      name: z.string(),
      count: z.number(),
    })
  ),
});
export type BookingPattern = z.infer<typeof BookingPatternSchema>;

/**
 * Vendor Customer - Main entity with all metrics
 */
export const VendorCustomerSchema = z.object({
  id: z.string(),
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  avatar: z.string().nullish(),

  // Lifetime Value Metrics
  totalSpend: z.number(),
  visitCount: z.number(),
  avgSpendPerVisit: z.number(),
  daysSinceLastVisit: z.number().nullish(),
  lastVisitDate: z.string().nullish(),
  firstVisitDate: z.string(),

  // Segmentation
  segment: CustomerSegmentEnum,

  // Credit / Pending balance
  pendingAmount: z.number().optional(),

  // Booking Patterns (optional, loaded on demand)
  bookingPatterns: BookingPatternSchema.optional(),
});
export type VendorCustomer = z.infer<typeof VendorCustomerSchema>;

/**
 * Paginated Customer List Response
 */
export const VendorCustomerListResponseSchema = z.object({
  customers: z.array(VendorCustomerSchema),
  pagination: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
  }),
  summary: z.object({
    totalCustomers: z.number(),
    newCount: z.number(),
    returningCount: z.number(),
    vipCount: z.number(),
    atRiskCount: z.number(),
    avgLifetimeValue: z.number(),
  }),
  period: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
});
export type VendorCustomerListResponse = z.infer<typeof VendorCustomerListResponseSchema>;

/**
 * Filter DTO for customer list queries
 */
export const VendorCustomerFilterSchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  segment: CustomerSegmentEnum.optional(),
  search: z.string().optional(), // Search by name/email/phone
  sortBy: z
    .enum(['totalSpend', 'visitCount', 'lastVisitDate', 'firstName'])
    .optional()
    .default('totalSpend'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  page: z.number().optional().default(1),
  limit: z.number().optional().default(20),
});
export type VendorCustomerFilter = z.infer<typeof VendorCustomerFilterSchema>;

/**
 * Admin Top Customers Summary (for dashboard card)
 */
export const AdminTopCustomersSummarySchema = z.object({
  totalUniqueCustomers: z.number(),
  newCustomersCount: z.number(),
  returningCustomersCount: z.number(),
  newVsReturningRatio: z.number(),
  avgCustomerLTV: z.number(),
  topCustomers: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        totalSpend: z.number(),
        bookingCount: z.number(),
      })
    )
    .max(5),
});
export type AdminTopCustomersSummary = z.infer<typeof AdminTopCustomersSummarySchema>;

/**
 * Send Notification Request
 */
export const SendCustomerNotificationRequestSchema = z.object({
  customerIds: z.array(z.string()).min(1),
  title: z.string().min(1).max(100),
  message: z.string().min(1).max(500),
});
export type SendCustomerNotificationRequest = z.infer<typeof SendCustomerNotificationRequestSchema>;

/**
 * Send Notification Response
 */
export const SendCustomerNotificationResponseSchema = z.object({
  success: z.boolean(),
  sentCount: z.number(),
  failedCount: z.number(),
  message: z.string(),
});
export type SendCustomerNotificationResponse = z.infer<
  typeof SendCustomerNotificationResponseSchema
>;
